import API from "../Services/API";
import Model from "./Model"

const useAvailableMetrics = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'availableMetrics',
        stateKey: ['reports', 'availableMetrics'],
        fetchData: () => API.get('/reports/metrics'),
    });
}

const reports = {
    'end-of-day': {
        title: 'End of day',
        question: 'What did everyone do and take?',
        icon: 'Wallet',
        keywords: ['end of day', 'cash up', 'cash', 'takings', 'today', 'workers', 'staff'],
        dateRange: {preference: 'reports:end-of-day:dateRange', default: 'today', ranges: 'days'},
        controls: ['dateRange', 'userId'],
        metrics: [
            {name: 'paymentsTotal', options: {paymentStatistic: 'gross'}, compare: true},
            {name: 'cashTakenTotal', compare: true},
            {name: 'workCompletedTotal', options: {statistic: 'cost'}, compare: true},
            {name: 'workCompletedTotal', options: {statistic: 'count'}, compare: true},
            {name: 'takingsByWorker', width: 'full'},
            {name: 'paymentsList', width: 'full'},
            {name: 'workDoneList', width: 'full'},
        ],
    },
    'money-in': {
        title: 'Money in',
        question: 'How much has come in?',
        icon: 'MoneyBillWave',
        keywords: ['payments', 'takings', 'income', 'earned', 'cash', 'card', 'fees', 'charges', 'after fees'],
        controls: ['dateRange', 'dateInterval', 'paymentStatistic', 'userId'],
        metrics: [
            {name: 'paymentsTotal', options: {paymentStatistic: 'gross'}, compare: true},
            {name: 'paymentsTotal', options: {paymentStatistic: 'fees'}, compare: true, goodDirection: 'down'},
            {name: 'paymentsTotal', options: {paymentStatistic: 'net'}, compare: true},
            {name: 'cashTakenTotal', compare: true},
            {name: 'paymentsOverTime', width: 'full'},
            {name: 'paymentsByMethod'},
            {name: 'paymentsByStatus'},
            {name: 'feesByPaymentMethod', width: 'full'},
            {name: 'paymentsList', width: 'full'},
        ],
    },
    'who-owes-you': {
        title: 'Who owes you',
        question: 'Who hasn\'t paid yet?',
        icon: 'FileInvoiceDollar',
        keywords: ['owed', 'owing', 'debt', 'debtors', 'unpaid', 'overdue', 'aged'],
        controls: [],
        metrics: [
            {name: 'unpaidInvoicesTotal', options: {statistic: 'cost', due: 'all'}, goodDirection: 'down'},
            {name: 'unpaidInvoicesTotal', options: {statistic: 'cost', due: 'overdue'}, goodDirection: 'down'},
            {name: 'unpaidInvoicesTotal', options: {statistic: 'count', due: 'all'}, goodDirection: 'down'},
            {name: 'agedDebt'},
            {name: 'customerBalances'},
            {name: 'customersOwing', width: 'full'},
        ],
    },
    'profit-and-loss': {
        title: 'Profit and loss',
        question: 'Am I making money?',
        icon: 'ScaleUnbalanced',
        keywords: ['profit', 'loss', 'p&l', 'income', 'expenses', 'fees'],
        controls: ['dateRange', 'dateInterval', 'incomeBasis'],
        metrics: [
            {name: 'incomeTotal', compare: true},
            {name: 'expensesTotal', compare: true, goodDirection: 'down'},
            {name: 'paymentsTotal', options: {paymentStatistic: 'fees'}, compare: true, goodDirection: 'down'},
            {name: 'profitTotal', compare: true},
            {name: 'profitOverTime', width: 'full', chart: 'profit'},
            {name: 'expensesByCategory', options: {statistic: 'cost'}},
            {name: 'paymentsByMethod', options: {paymentStatistic: 'gross'}},
        ],
    },
    'invoices': {
        title: 'Invoices',
        question: 'What have I invoiced?',
        icon: 'FileInvoice',
        keywords: ['invoices', 'invoiced', 'billing'],
        controls: ['dateRange', 'dateInterval', 'invoiceStatistic'],
        metrics: [
            {name: 'invoicesTotal', compare: true},
            {name: 'invoicesOverTime', width: 'full'},
            {name: 'invoicesByStatus'},
            {name: 'invoicesByStatusOverTime', chart: 'stacked'},
        ],
    },
    'expenses': {
        title: 'Expenses',
        question: 'Where is the money going?',
        icon: 'Receipt',
        keywords: ['expenses', 'costs', 'spending'],
        controls: ['dateRange', 'dateInterval', 'statistic'],
        metrics: [
            {name: 'expensesTotal', compare: true, goodDirection: 'down'},
            {name: 'expensesOverTime', width: 'full'},
            {name: 'expensesByCategory'},
            {name: 'expensesByVendor'},
            {name: 'expensesByAccount'},
        ],
    },
    'work-done': {
        title: 'Work done',
        question: 'What got done, and its value?',
        icon: 'ClipboardCheck',
        keywords: ['work', 'jobs', 'completed', 'done', 'skipped'],
        controls: ['dateRange', 'dateInterval', 'statistic', 'userId'],
        metrics: [
            {name: 'workCompletedTotal', options: {statistic: 'cost'}, compare: true},
            {name: 'workCompletedTotal', options: {statistic: 'count'}, compare: true},
            {name: 'workCompletedTotal', options: {statistic: 'average'}, compare: true},
            {name: 'workSkippedTotal', options: {statistic: 'count'}, compare: true, goodDirection: 'down'},
            {name: 'workDoneOverTime', width: 'full'},
            {name: 'workDoneBy', options: {groupBy: 'round'}},
            {name: 'workDoneBy', options: {groupBy: 'service'}},
            {name: 'workDoneBy', options: {groupBy: 'customer'}},
            {name: 'workDoneBy', options: {groupBy: 'frequency'}},
            {name: 'workDoneList', width: 'full'},
        ],
    },
    'team': {
        title: 'Team',
        question: 'How is each person doing?',
        icon: 'Users',
        keywords: ['workers', 'staff', 'team', 'assigned', 'employees'],
        controls: ['dateRange', 'dateInterval', 'statistic'],
        metrics: [
            {name: 'plannedWorkByWorker', width: 'full'},
            {name: 'takingsByWorker', width: 'full'},
            {name: 'workDoneOverTime', options: {splitBy: 'worker'}, width: 'full'},
            {name: 'workDoneBy', options: {groupBy: 'worker'}},
            {name: 'costPerHourByWorker'},
            {name: 'averageRecordedTimeByWorker'},
            {name: 'timeAccuracyByWorker'},
        ],
    },
    'forecast': {
        title: 'Forecast',
        question: 'What work is coming up?',
        icon: 'CrystalBall',
        keywords: ['forecast', 'upcoming', 'expected', 'future'],
        dateRange: {preference: 'reports:forecast:dateRange', default: 'nextThreeMonths', ranges: 'future'},
        controls: ['dateRange', 'dateInterval', 'statistic'],
        metrics: [
            {name: 'forecastTotal', options: {statistic: 'cost'}},
            {name: 'forecastTotal', options: {statistic: 'count'}},
            {name: 'forecastOverTime', width: 'full', chart: 'stacked'},
            {name: 'forecastBy', options: {groupBy: 'round'}},
            {name: 'forecastBy', options: {groupBy: 'service'}},
        ],
    },
    'rounds': {
        title: 'Rounds',
        question: 'How do my rounds compare?',
        icon: 'Route',
        keywords: ['rounds', 'travel'],
        controls: ['dateRange'],
        metrics: [
            {name: 'recurringWorkBy', options: {groupBy: 'round', statistic: 'annual'}},
            {name: 'aggregateRoundsByStatistic', options: {statistic: 'jobCount'}},
            {name: 'aggregateRoundsByStatistic', options: {statistic: 'jobDuration'}},
            {name: 'aggregateRoundsByStatistic', options: {statistic: 'travelDuration'}},
            {name: 'workDoneBy', options: {groupBy: 'round'}},
        ],
    },
    'time-tracking': {
        title: 'Time tracking',
        question: 'How long do jobs take?',
        icon: 'Timer',
        keywords: ['time', 'hours', 'recorded', 'hourly rate'],
        controls: ['dateRange', 'dateInterval'],
        metrics: [
            {name: 'workCompletedTotal', options: {statistic: 'recordedTime'}, compare: true},
            {name: 'averageRecordedTimeTotal'},
            {name: 'recordedTimeOverTime', width: 'full'},
            {name: 'averageRecordedTimeByWorker'},
            {name: 'averageRecordedTimeByService'},
            {name: 'costPerHourByWorker'},
            {name: 'costPerHourByService'},
            {name: 'timeAccuracyByWorker'},
            {name: 'timeAccuracyByService'},
        ],
    },
    'customer-base': {
        title: 'Customer base',
        question: 'What is my customer base worth?',
        icon: 'UsersGear',
        keywords: ['customers', 'jobs', 'average price', 'frequency', 'value'],
        controls: [],
        metrics: [
            {name: 'activeCustomersTotal'},
            {name: 'averageJobPrice', options: {statistic: 'count'}},
            {name: 'averageJobPrice', options: {statistic: 'average'}},
            {name: 'recurringRevenueTotal', options: {period: 'annual'}},
            {name: 'recurringWorkBy', options: {groupBy: 'frequency', statistic: 'count'}},
            {name: 'recurringWorkBy', options: {groupBy: 'frequency', statistic: 'annual'}},
            {name: 'recurringWorkBy', options: {groupBy: 'service', statistic: 'annual'}},
            {name: 'recurringWorkBy', options: {groupBy: 'round', statistic: 'annual'}},
            {name: 'aggregateJobsByStatus', options: {statistic: 'count'}},
        ],
    },
    'customer-growth': {
        title: 'Customer growth',
        question: 'Am I gaining or losing customers?',
        icon: 'ChartLine',
        keywords: ['customers', 'growth', 'churn', 'new customers'],
        controls: ['dateRange', 'dateInterval'],
        metrics: [
            {name: 'customerGrowthChangeOverTime', width: 'full'},
            {name: 'activeCustomersOverTime', chart: 'line'},
            {name: 'jobsAddedOverTime'},
        ],
    },
    'recurring-revenue': {
        title: 'Recurring revenue',
        question: 'Is my regular work growing?',
        icon: 'ArrowsUpDown',
        keywords: ['recurring', 'revenue', 'arr', 'mrr'],
        note: {
            preference: 'reports-recurring-revenue-help-dismissed',
            message: 'Annual Recurring Revenue (ARR) is a useful way to see how healthy your business is. It\'s based on the annualised value of repeat jobs only, not one-off work or money actually paid. By tracking changes in ARR, you can see whether you\'re growing or shrinking.',
        },
        controls: ['dateRange', 'dateInterval', 'service'],
        metrics: [
            {name: 'recurringRevenueTotal', options: {period: 'annual'}},
            {name: 'recurringRevenueTotal', options: {period: 'monthly'}},
            {name: 'businessArrOverTime', width: 'full', chart: 'line'},
            {name: 'recurringRevenueChangeOverTime', width: 'full'},
            {name: 'activeCustomersByService'},
        ],
    },
    'services': {
        title: 'Services',
        question: 'Which services bring the work in?',
        icon: 'Toolbox',
        keywords: ['services'],
        controls: ['dateRange', 'dateInterval', 'statistic'],
        metrics: [
            {name: 'workDoneOverTime', options: {splitBy: 'service'}, width: 'full', chart: 'stacked'},
            {name: 'workDoneBy', options: {groupBy: 'service'}},
            {name: 'aggregateJobsByService'},
            {name: 'activeCustomersByService'},
            {name: 'recurringWorkBy', options: {groupBy: 'service', statistic: 'average'}},
        ],
    },
    'quotes': {
        title: 'Quotes',
        question: 'Are quotes turning into work?',
        icon: 'FileLines',
        keywords: ['quotes', 'leads', 'conversion', 'won'],
        controls: ['dateRange', 'dateInterval'],
        metrics: [
            {name: 'quotesTotal', compare: true},
            {name: 'quoteWinRate', compare: true},
            {name: 'openQuotesNeedingPricing', goodDirection: 'down'},
            {name: 'quotesOverTime', width: 'full', chart: 'stacked'},
            {name: 'quotesBy', options: {groupBy: 'outcome'}},
            {name: 'quotesBy', options: {groupBy: 'source'}},
        ],
    },
    'website': {
        title: 'Website',
        question: 'Is my website getting visits?',
        icon: 'GlobePointer',
        keywords: ['website', 'visitors', 'views'],
        controls: ['dateRange', 'dateInterval'],
        metrics: [
            {name: 'websitePageViewsTotal'},
            {name: 'websiteVisitorsTotal'},
            {name: 'websiteVisitorsOverTime', width: 'full'},
            {name: 'websitePageViewsOverTime', width: 'full'},
        ],
    },
};

const reportSections = [
    {
        title: 'Money',
        reports: ['end-of-day', 'money-in', 'who-owes-you', 'profit-and-loss', 'invoices', 'expenses'],
    },
    {
        title: 'Work',
        reports: ['work-done', 'team', 'forecast', 'rounds', 'time-tracking'],
    },
    {
        title: 'Customers',
        reports: ['customer-base', 'customer-growth', 'recurring-revenue', 'services'],
    },
    {
        title: 'New work',
        reports: ['quotes', 'website'],
    },
];

const reportHighlights = [
    {report: 'money-in', metric: {name: 'paymentsTotal', options: {dateRange: 'thisMonth', paymentStatistic: 'gross'}, compare: true}},
    {report: 'work-done', metric: {name: 'workCompletedTotal', options: {dateRange: 'thisMonth', statistic: 'cost'}, compare: true}},
    {report: 'who-owes-you', metric: {name: 'unpaidInvoicesTotal', options: {statistic: 'cost', due: 'all'}, goodDirection: 'down'}},
];

const metricOptions = {
    dateInterval: {
        label: 'Grouped',
        icon: 'CalendarWeek',
        choices: [
            {value: 'auto', label: 'Automatically'},
            {value: 'Date', label: 'By day'},
            {value: 'Week', label: 'By week'},
            {value: 'Month', label: 'By month'},
            {value: 'Year', label: 'By year'},
        ],
    },
    statistic: {
        label: 'Show',
        icon: 'Slider',
        choices: [
            {value: 'cost', label: 'Value'},
            {value: 'count', label: 'Count'},
            {value: 'average', label: 'Average value'},
            {value: 'annual', label: 'Yearly value'},
            {value: 'recordedTime', label: 'Time recorded'},
            {value: 'averageRecordedTime', label: 'Average time recorded'},
            {value: 'annualValue', label: 'Yearly value'},
            {value: 'jobCount', label: 'Jobs'},
            {value: 'jobCost', label: 'Job value'},
            {value: 'jobDuration', label: 'Job time'},
            {value: 'travelDuration', label: 'Travel time'},
        ],
    },
    paymentStatistic: {
        label: 'Show',
        icon: 'Slider',
        choices: [
            {value: 'gross', label: 'Money in'},
            {value: 'fees', label: 'Fees'},
            {value: 'net', label: 'After fees'},
            {value: 'count', label: 'Number of payments'},
        ],
    },
    invoiceStatistic: {
        label: 'Show',
        icon: 'Slider',
        choices: [
            {value: 'total', label: 'Invoiced'},
            {value: 'count', label: 'Number of invoices'},
        ],
    },
    incomeBasis: {
        label: 'Income from',
        icon: 'SterlingSign',
        choices: [
            {value: 'payments', label: 'Money in'},
            {value: 'invoiced', label: 'Invoiced'},
            {value: 'workDone', label: 'Work done'},
        ],
    },
    groupBy: {
        label: 'Group by',
        icon: 'LayerGroup',
        choices: [
            {value: 'worker', label: 'Worker'},
            {value: 'round', label: 'Round'},
            {value: 'service', label: 'Service'},
            {value: 'frequency', label: 'How often'},
            {value: 'customer', label: 'Customer'},
            {value: 'outcome', label: 'Status'},
            {value: 'source', label: 'Source'},
        ],
    },
    splitBy: {
        label: 'Split by',
        icon: 'LayerGroup',
        choices: [
            {value: null, label: 'Nothing'},
            {value: 'worker', label: 'Worker'},
            {value: 'round', label: 'Round'},
            {value: 'service', label: 'Service'},
        ],
    },
    period: {
        label: 'Per',
        icon: 'Calendar',
        choices: [
            {value: 'annual', label: 'Year'},
            {value: 'monthly', label: 'Month'},
        ],
    },
    due: {
        label: 'Invoices',
        icon: 'FileInvoice',
        choices: [
            {value: 'all', label: 'All unpaid'},
            {value: 'overdue', label: 'Overdue'},
        ],
    },
    feeType: {
        label: 'Fees',
        icon: 'Receipt',
        choices: [
            {value: 'soapy', label: 'Soapy fees'},
            {value: 'stripe', label: 'Stripe fees'},
            {value: 'total', label: 'Total fees'},
            {value: 'rate', label: 'Fee rate'},
        ],
    },
    planFilter: {
        label: 'Plans',
        icon: 'Filter',
        choices: [
            {value: 'all', label: 'All plans'},
            {value: 'active', label: 'Active plans'},
            {value: 'paid', label: 'Paid plans'},
            {value: 'legacy', label: 'Legacy plans'},
        ],
    },
    userId: {
        label: 'Worker',
        icon: 'UserHelmetSafety',
        allLabel: 'Everyone',
    },
    service: {
        label: 'Service',
        icon: 'Toolbox',
        allLabel: 'All services',
    },
    attentionStatus: {
        label: 'Attention status',
        icon: 'Flag',
        allLabel: 'Waiting on an admin',
    },
};

const metricTags = {
    Admin: 'Soapy admin',
    Assistant: 'Assistant',
    Balances: 'Balances',
    Customers: 'Customers',
    Expenses: 'Expenses',
    Invoices: 'Invoices',
    Jobs: 'Jobs',
    Payments: 'Payments',
    Profit: 'Profit',
    Quotes: 'Quotes',
    Revenue: 'Revenue',
    Rounds: 'Rounds',
    Services: 'Services',
    Users: 'Users',
    Websites: 'Website',
    TimeTracking: 'Time tracking',
    Work: 'Work',
    Workers: 'Team',
};

const changeTypeColours = {
    'New': 'gain1',
    'Reactivation': 'gain2',
    'Expansion': 'gain3',
    'Planned Increase': 'gain4',
    'Churn': 'loss1',
    'Contraction': 'loss2',
    'Planned Decrease': 'loss3',
};

export {
    useAvailableMetrics,
    reports,
    reportSections,
    reportHighlights,
    metricOptions,
    metricTags,
    changeTypeColours,
};
